import { Users, Zap, Clock, FolderOpen } from "lucide-react";
import heroBundle from "@/assets/hero-bundle.webp";
import paymentBadges from "@/assets/payment-badges.webp";
import CTAButton from "./CTAButton";
import PriceDisplay from "./PriceDisplay";
import { useMetaPixel, MetaPixelEvents } from "@/hooks/useMetaPixel";
import { useEffect, memo } from "react";

const HeroSection = memo(() => {
  const { trackEvent } = useMetaPixel();

  useEffect(() => {
    // Track ViewContent on page load - deferred to not block render
    // Use setTimeout as fallback since requestIdleCallback is not supported on iOS/Safari
    const timeoutId = setTimeout(() => {
      trackEvent(MetaPixelEvents.VIEW_CONTENT, {
        content_name: "3D STL Bundle",
        content_category: "Digital Product",
      });
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <section className="py-12 md:py-20">
      <div className="container text-center">
        {/* Social proof badge */}
        <div className="badge-outline inline-flex mb-8">
          <Users className="w-4 h-4" />
          <span>1,200+ makers already using</span>
        </div>

        {/* Main headline */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-4">
          <span className="text-primary">30TB Premium</span>
          <br />
          <span className="text-foreground">3D STL Files</span>
        </h1>

        {/* Subheadline */}
        <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-8">
          The ultimate collection of ready-to-print 3D models. From anime figures to movie collectibles, everything you need to start printing and selling.
        </p>

        {/* Feature badges */}
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          <div className="feature-badge">
            <Zap className="w-4 h-4 text-primary" />
            <span>Ready-to-Print</span>
          </div>
          <div className="feature-badge">
            <Clock className="w-4 h-4 text-primary" />
            <span>Lifetime Access</span>
          </div>
          <div className="feature-badge">
            <FolderOpen className="w-4 h-4 text-primary" />
            <span>Organized by Category</span>
          </div>
        </div>

        {/* Hero image - LCP element */}
        <div className="max-w-3xl mx-auto mb-10">
          <img
            src={heroBundle}
            alt="30TB Premium 3D STL Files Bundle Preview"
            className="w-full h-auto hero-image"
            width={768}
            height={768}
            loading="eager"
            fetchPriority="high"
            decoding="sync"
          />
        </div>

        {/* Pricing */}
        <PriceDisplay className="flex items-center justify-center gap-3 mb-6" />

        {/* CTA Button */}
        <CTAButton className="mb-4">
          <span className="flex flex-col leading-tight">
            <span>Download Now</span>
            <span>Offer Ends Soon 75% Off 🚨!</span>
          </span>
        </CTAButton>

        {/* Payment badges */}
        <div className="flex justify-center">
          <img
            src={paymentBadges}
            alt="Secure payment methods"
            className="h-8 md:h-10 w-auto"
            width={1024}
            height={67}
            loading="lazy"
          />
        </div>

        {/* Limited time notice */}
        <div className="mt-8 inline-flex items-center gap-2 text-accent font-semibold limited-time">
          <span>⏳</span>
          <span>LIMITED TIME DEAL – Ends Soon!</span>
        </div>

        <p className="text-muted-foreground mt-4">
          Immediate Access to 30TB Premium 3D STL Files.
        </p>
      </div>
    </section>
  );
});

HeroSection.displayName = "HeroSection";

export default HeroSection;
